<template>
    <div class="flex flex-col h-full w-full gap-2 px-2">
        <ai-tree-node-load
            ref="eoschemaFullRef"
            :meta="meta"
            :select-func-params-call-back="selectFuncParamsCallBack"
            :toolbar-user-items="toolbarUserItems"
            @toolbar-button-click="toolbarButtonClick"
        />
        <dx-popup
            ref="popupRef"
            :hide-on-outside-click="true"
            :show-close-button="true"
            :show-title="true"
            :visible="popupVisible"
            height="95%"
            title="Выбор присоединения для связывания ЭО"
            @hidden="changePopupVisibility(false)"
            @shown="onPopupShown"
        >
            <ai-grid-virtual-base
                ref="gridInstance"
                :auto-load-data="virtualGridMeta.autoLoadData || false"
                :customContextMenu="virtualGridMeta.customContextMenu"
                :meta="virtualGridMeta"
                :to-remove-toolbar-items-names="virtualGridMeta.toRemoveToolbarItemsNames"
                selectionMode="single"
                @toolbar-button-click="toolbarButtonClick"
            />
        </dx-popup>
    </div>
</template>

<script setup>
import AiTreeNodeLoad from '@/components/ai-tree-node-load/ai-tree-node-load.vue';
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { DxPopup } from 'devextreme-vue';
import { commitDataChanges } from '@/service/meta-service';
import { dxUiConfirm, dxUiNotify } from '@/utils/dx-helper';
import AiGridVirtualBase from '@/components/ai-grid-virtual/ai-grid-virtual-base.vue';

const route = useRoute();
const eoschemaFullRef = ref();
const rootNodeData = ref();
const gridInstance = ref();
const popupVisible = ref(false);

const meta = ref({
    api: 'e-grid./eoschemafull/get',
    keyExpr: 'eobj_id',
    keyParent: 'supply_id',
    pathField: 'path',
    hasItemsField: 'has_items',
    selectFuncParams: {
        src_id: route.params.id,
    },
    firstColumnFields: {
        text: 'name',
        svg: 'asset_svg',
        caption: 'Диспетчерский №',
        width: 200,
    },
    columns: [
        {
            field: 'src_id',
            caption: 'src_id',
            visible: false,
        }, // width: 350,},
        {
            field: 'sv',
            caption: 'Напряжение, кВ',
            width: 80,
        },
        {
            field: 'has_pnts',
            caption: 'Точки учета',
            width: 80,
        },
        {
            field: 'has_attps',
            caption: 'Точки присоединения',
            visible: false,
        },
        {
            field: 'bname',
            caption: 'Балансовая принадлежность',
            width: 300,
        },
        {
            field: 'ename',
            caption: 'Эксплуатационная ответственность',
            width: 300,
        },
        {
            field: 'distrobj_kindname',
            caption: 'Вид энергообъекта',
            width: 80,
        },
        {
            field: 'distrobjname',
            caption: 'Энергообъект',
            width: 100,
        },
        {
            field: 'bname_eo',
            caption: 'Балансовая принадлежность объекта',
            width: 300,
        },
        {
            field: 'ename_eo',
            caption: 'Эксплуатационная ответственность объекта',
            width: 300,
        },
        {
            field: 'imported',
            caption: 'Импортирован',
            data_type: 'boolean',
        },
        {
            field: 'sx',
            caption: 'Схема питания',
        },
        {
            field: 'tech_location_code',
            caption: 'Код технического места оборудования в SAP (или ее аналога)',
            visible: false,
        },
        {
            field: 'path',
            caption: 'path',
            visible: false,
        },
    ],
    columnsLikes: ['name', 'sv', 'bname', 'ename', 'distrobjname', 'ename_eo'],
    sort: {
        field: 'name',
        order: 'asc',
    },
});
const toolbarUserItems = ref([
    {
        widget: 'dx-button',
        index: 1,
        component: {
            name: 'bind',
            hint: 'Связать',
            htmlElement: 'ai-add-base',
        },
    },
    {
        widget: 'dx-button',
        index: 2,
        component: {
            name: 'unbind',
            hint: 'Отвязать',
            htmlElement: 'ai-delete-base',
        },
    },
]);
const virtualGridMeta = {
    displayExpr: 'name',
    valueExpr: 'eobj_id',
    columns: [
        {
            field: 'eobj_id',
            text: 'ID',
            visible: false,
        },
        {
            field: 'src_kindname',
            text: 'Вид энергообъекта',
            width: 80,
        },
        {
            field: 'src_dnum',
            text: 'Диспетчерский № энергообъекта',
            width: 120,
        },
        {
            field: 'src_setiname_str',
            text: 'Зона филиала энергообъекта',
            width: 120,
        },
        {
            field: 'src_bname',
            text: 'Балансовая принадлежность энергообъекта',
            width: 120,
        },
        {
            field: 'dnum',
            text: 'Диспетчерский №',
            width: 120,
        },
        {
            field: 'fname',
            text: 'Оперативное наимен.',
            width: 120,
        },
        {
            field: 'tlinename',
            text: 'Исполнение линии',
            width: 80,
        },
        {
            field: 'bname',
            text: 'Балансовая принадлежность',
            width: 120,
        },
        {
            field: 'ename',
            text: 'Эксплуатационная ответственность',
            width: 120,
        },
        {
            field: 'nv',
            text: 'Напряжение, кВ',
            width: 60,
        },
        {
            field: 'imported',
            text: 'Импортирован',
            width: 60,
            alignment: 'center',
        },
        {
            field: 'coment',
            text: 'Комментарий',
            width: 120,
        },
        {
            field: 'kindname',
            text: 'Вид оборудования',
            width: 60,
        },
        {
            field: 'src_sname',
            text: 'Оперативное наименование энергообъекта',
            width: 120,
        },
        {
            field: 'src_fname',
            text: 'Полное наименование энергообъекта',
            width: 120,
        },
        {
            field: 'src_ename',
            text: 'Эксплуатационная ответственность энергообъекта',
        },
        {
            field: 'bal_id',
            text: 'bal_id',
            visible: false,
        },
        {
            field: 'bkod_separator',
            text: 'bkod_separator',
            visible: false,
        },
        {
            field: 'bkod_ecls',
            text: 'bkod_ecls',
            visible: false,
        },
        {
            field: 'expl_id',
            text: 'expl_id',
            visible: false,
        },
        {
            field: 'ekod_separator',
            text: 'ekod_separator',
            visible: false,
        },
        {
            field: 'kod_ecls',
            text: 'kod_ecls',
            visible: false,
        },
        {
            field: 'kod_v',
            text: 'kod_v',
            visible: false,
        },
        {
            field: 'tech_location_code_cur',
            text: 'Код технического места оборудования в SAP (или ее аналога)',
            visible: false,
        },
        {
            field: 'tech_location_code',
            text: 'Код технического места ЛЭП в SAP (или ее аналога)',
            visible: false,
        },
        {
            field: 'kod_line',
            text: 'kod_line',
            visible: false,
        },
        {
            field: 'deleted',
            text: 'deleted',
            visible: false,
        },
        {
            field: 'src_id',
            text: 'src_id',
            visible: false,
        },
        {
            field: 'src_kodp_seti',
            text: 'src_kodp_seti',
            visible: false,
        },
        {
            field: 'src_bal_id',
            text: 'src_bal_id',
            visible: false,
        },
        {
            field: 'src_expl_id',
            text: 'src_expl_id',
            visible: false,
        },
    ],
    loadUrl: 'e-grid./eofider-src-x/data/get',
    keyExpr: 'eobj_id',
    customContextMenu: [
        {
            name: 'gotoCard',
            visible: false,
        },
    ],
    toRemoveToolbarItemsNames: ['copy', 'edit', 'add', 'delete', 'save'],
    api: {
        save: 'e-grid./eofider-src-x/data/set',
    },
    args: {
        save: {},
    },
};
onMounted(() => {
    const listInstance = eoschemaFullRef.value.treeListRef.instance;
    listInstance.on('cellPrepared', onTreeListCellPrepared);
    listInstance.on('focusedRowChanged', onFocusedRowChanged);
    //cellPrepared
});
const onTreeListCellPrepared = event => {
    if (event.rowType === 'data' && !event.data.supply_id) {
        event.cellElement.style.fontWeight = 'bold';
    }
};
const onFocusedRowChanged = event => {
    if (event?.row?.data) {
        eoschemaFullRef.value.setStatusMessage(event?.row?.data['sx']);
    }
};
const toolbarButtonClick = async e => {
    if (e.item.component.name === 'bind') {
        onBindClick();
    } else if (e.item.component.name === 'unbind') {
        onUnbindClick();
    } else if (e.item.component.name === 'choice') {
        const focusedRowKey = e.data.selectedRowKeys[0];
        if (focusedRowKey) {
            const confirmResult = await dxUiConfirm('Вы уверены, что хотите создать связь?', 'Подтвердите действие');
            if (confirmResult) {
                const processedData = {
                    insertDataArgs: [],
                    update_args: rootNodeData.value,
                    updateDataArgs: [
                        gridInstance.value?.gridInstance
                            .option('dataSource')
                            .items()
                            .find(row => row[virtualGridMeta.keyExpr] === focusedRowKey),
                    ],
                    deleteDataArgs: [],
                };
                if (processedData.updateDataArgs.length > 0) {
                    const { errors } = await commitDataChanges(
                        virtualGridMeta.api.save,
                        processedData,
                        virtualGridMeta.args.save
                    );
                    //Про валидацию пока забыли
                    if (errors.length > 0) {
                        dxUiNotify(`Ошибка базы данных: ${errors.map(error => error.message).join('\n')}`, 'error');
                    } else {
                        dxUiNotify('Изменения данных прошли успешно', 'success');
                        changePopupVisibility(false);
                    }
                }
            }
        }
    }
};
const selectFuncParamsCallBack = args => {
    if (args.option === 'roots') {
        return {
            src_id: route.params.id,
        };
    } else if (args.option === 'childes') {
        return {
            eobj_id: args.keyExprValue,
        };
    } else if (args.option === 'find') {
        return {
            src_id: route.params.id,
            sel_all: true,
        };
    }
};
const changePopupVisibility = value => (popupVisible.value = value);
const onPopupShown = () => {
    gridInstance.value.loadData();
};
const onBindClick = () => {
    const listInstance = eoschemaFullRef.value.treeListRef.instance;
    const focusedRowKey = listInstance.option('focusedRowKey');
    if (listInstance.getRootNode()?.children) {
        if (
            focusedRowKey &&
            listInstance
                .getRootNode()
                .children.map(c => c.key)
                .includes(focusedRowKey)
        ) {
            rootNodeData.value = listInstance.getRootNode().children.find(c => c.key === focusedRowKey).data;
            changePopupVisibility(true);
        }
    }
};
const onUnbindClick = async () => {
    const listInstance = eoschemaFullRef.value.treeListRef.instance;
    const focusedRowKey = listInstance.option('focusedRowKey');
    if (listInstance.getRootNode()?.children) {
        if (
            focusedRowKey &&
            listInstance
                .getRootNode()
                .children.map(c => c.key)
                .includes(focusedRowKey)
        ) {
            const confirmResult = await dxUiConfirm('Вы уверены, что хотите разорвать связь?', 'Подтвердите действие');
            if (confirmResult) {
                rootNodeData.value = listInstance.getRootNode().children.find(c => c.key === focusedRowKey).data;
                const processedData = {
                    insertDataArgs: [],
                    updateDataArgs: [],
                    deleteDataArgs: [rootNodeData.value],
                };
                if (processedData.deleteDataArgs.length > 0) {
                    const { errors } = await commitDataChanges(
                        virtualGridMeta.api.save,
                        processedData,
                        virtualGridMeta.args.save
                    );
                    //Про валидацию пока забыли
                    if (errors.length > 0) {
                        dxUiNotify(`Ошибка базы данных: ${errors.map(error => error.message).join('\n')}`, 'error');
                    } else {
                        dxUiNotify('Изменения данных прошли успешно', 'success');
                        changePopupVisibility(false);
                    }
                }
            }
        }
    }
};
</script>
<script>
export default {
    name: 'eoschema-full-tnl',
};
</script>

<style scoped></style>
